import { useCallback, useEffect, useRef, useState } from 'react'

import { Progress } from '@agro-club/agroclub-shared'

import { useNoPermissionNotification } from 'hooks/useNoPermissionNotification'
import { getTableData } from 'modules/domain/common/managers'
import { ListRequestParams } from 'modules/domain/types'
import { RequestError } from 'modules/errors'
import { apiClient } from 'modules/utils/httpClient'
import { useNavigate } from 'react-router-dom'
import CommonRoutes from 'views/pages/commonRoutes'

export type refetchFunc = (silent?: boolean) => Promise<void>

export type TableDataObject<T> = {
  progress: Progress
  data: T[] | undefined
  total: number
  pageSize: number
  refetch: refetchFunc
  next?: number
  previous?: number
}

export const useTableData: <T = any>(
  endpoint: string,
  params: ListRequestParams,
  isFiltersLoaded: boolean,
) => TableDataObject<T> = <T = any>(endpoint: string, params: ListRequestParams, isFiltersLoaded: boolean) => {
  const [progress, setProgress] = useState(Progress.IDLE)
  const [data, setData] = useState<T[]>()
  const [total, setTotal] = useState(0)
  const [pageSize, setPageSize] = useState(0)
  const abortControllerRef = useRef<AbortController | null>(null)
  const notify = useNoPermissionNotification()

  const fetchData = async (silent = false) => {
    if (!silent) {
      setProgress(Progress.WORK)
    }

    /* it is necessary to cancel the previous request because of race condition */
    abortControllerRef.current?.abort()

    abortControllerRef.current = new AbortController()

    try {
      const { list, total, pageSize = 0 } = await getTableData(endpoint, params, abortControllerRef.current)

      setData(list)
      setTotal(total)
      setPageSize(pageSize)
      setProgress(Progress.SUCCESS)
    } catch (err) {
      const error = err as RequestError
      if (error?.code === 403) {
        notify(endpoint)
      }
      // ignore errors due to non existing page
      // if page doesn't exists, server returns 404. usually it happens when user close tasks and the list size reducing
      if (error?.code === 404 && params.page > 1) {
        setData([])
        setProgress(Progress.SUCCESS)
      }
    } finally {
      abortControllerRef.current = null
    }
  }
  useEffect(() => {
    if (isFiltersLoaded) fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, isFiltersLoaded, params])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFetch = useCallback(fetchData, [endpoint, params])

  return {
    progress,
    data,
    total,
    pageSize,
    refetch: memoizedFetch,
  }
}

export const useSingleEntity = function <T>(
  url?: string,
  auto404 = true,
): readonly [Progress, T | undefined, refetchFunc] {
  const [progress, setProgress] = useState(Progress.IDLE)
  const [data, setData] = useState<T>()
  const navigate = useNavigate()
  const notify = useNoPermissionNotification()

  const getEntity = async (silent = false) => {
    try {
      !silent && setProgress(Progress.WORK)

      const data = await apiClient.get<T>(url as string)

      setData(data)

      !silent && setProgress(Progress.SUCCESS)
    } catch (err) {
      // TS fires an error that err is type of unknown
      // So, workaround is to convert it to the RequestError manually
      const error = err as RequestError

      if (auto404 && error?.code === 404) {
        navigate({ pathname: CommonRoutes.NotFound })
        return
      }

      if (error?.code === 403) {
        notify(url)
      }

      !silent && setProgress(Progress.ERROR)
    }
  }

  useEffect(() => {
    if (url) {
      getEntity()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFetch = useCallback(getEntity, [url])

  return [progress, data, memoizedFetch]
}

export const usePagination = <T>(items: T[], itemsPerPage: number): [number, (number) => void, T[]] => {
  const [currentPage, setCurrentPage] = useState(1)
  const lastIndex = currentPage * itemsPerPage
  const firstIndex = lastIndex - itemsPerPage
  const currentItems = items.slice(firstIndex, lastIndex)

  useEffect(() => {
    if (!currentItems.length && currentPage > 1) {
      setCurrentPage((prev) => prev - 1)
    }
  }, [currentItems.length, currentPage, items.length])

  return [currentPage, setCurrentPage, currentItems]
}
